import { useQuery, useQueryClient } from '@tanstack/react-query';
import { route } from 'preact-router';

import { T, btnIcon, card } from '@/ui/tokens';
import { Icons, sized } from '@/ui/icons';
import { TopBar } from '@/ui/primitives/TopBar';
import { CoinBalance } from '@/ui/primitives/CoinBalance';
import { Spinner } from '@/ui/primitives/Spinner';
import { ErrorBox } from '@/ui/primitives/ErrorBox';
import { homeRepo } from '@/data/repositories/home-repo';
import { openExternal } from '@/data/platform/links';
import { fmt } from '@/ui/utils/format';
import { useAuthInfo, useLogout } from '@/features/auth/useAuth';
import { ReferralPrompt } from '@/features/referrals/ReferralPrompt';
import { config } from '@/config';

export function HomeScreen() {
  const auth = useAuthInfo();
  const home = useQuery({
    queryKey: ['home'],
    queryFn: homeRepo.home,
    refetchInterval: config.refresh.homeMs,
  });
  const user = auth.data?.me;
  const provider = formatProvider(user?.provider);
  const logout = useLogout();

  // Deposit & withdraw always open in an external browser tab — platform
  // policies (Apple / Google / Telegram) prohibit in-app crypto purchases.
  // The links are signed and expire (WalletLinks:LinkTtlMinutes, 60 min), so keep
  // the cached copy well inside that while the app stays open or comes back.
  const qc = useQueryClient();
  const walletLinks = useQuery({
    queryKey: ['walletLinks'],
    queryFn: homeRepo.walletLinks,
    staleTime: 60_000,
    refetchInterval: 10 * 60_000,
    refetchOnWindowFocus: true,
  });

  const goWallet = (kind: 'deposit' | 'withdraw') => async () => {
    const pick = (l?: { deposit?: string | null; withdraw?: string | null }) =>
      kind === 'deposit' ? l?.deposit : l?.withdraw;
    let url = pick(walletLinks.data);
    if (!url || linkExpiresSoon(url)) {
      const fresh = await qc.fetchQuery({ queryKey: ['walletLinks'], queryFn: homeRepo.walletLinks, staleTime: 0 });
      url = pick(fresh);
    }
    if (url) openExternal(url);
  };

  return (
    <div style={{ background: T.bg, minHeight: '100%' }}>
      <TopBar
        title={user?.displayname ? `Hey, ${user.displayname}` : 'Welcome back'}
        sub={user?.email ?? provider}
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btnIcon} aria-label="News" onClick={() => route(config.routes.news)}>
              {sized(Icons.bell, 18)}
            </button>
            <button style={btnIcon} aria-label="Settings" onClick={() => route(config.routes.settings)}>
              {sized(Icons.gear, 18)}
            </button>
          </div>
        }
      />

      {home.isError && (
        <ErrorBox message="Couldn't load dashboard." onRetry={() => home.refetch()}/>
      )}

      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{
          ...card, padding: 22, position: 'relative', overflow: 'hidden',
          background: `radial-gradient(120% 100% at 0% 0%, rgba(198,255,61,0.10), transparent 50%), ${T.surface}`,
          border: `1px solid ${T.line2}`,
        }}>
          <div style={{
            position: 'absolute', top: -40, right: -40, width: 160, height: 160,
            borderRadius: '50%', background: T.accent, opacity: 0.06, filter: 'blur(20px)',
          }}/>
          {home.isPending ? <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}><Spinner/></div> : (
            <CoinBalance amount={home.data?.coins ?? 0} large label="Coin Balance"/>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button onClick={goWallet('deposit')} style={{
              flex: 1, padding: '14px', borderRadius: 14, border: 'none',
              background: T.accent, color: T.accentInk,
              fontFamily: T.font, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              {sized(Icons.down, 16, 2)}
              Deposit
            </button>
            <button onClick={goWallet('withdraw')} style={{
              flex: 1, padding: '14px', borderRadius: 14,
              background: 'transparent', color: T.text, border: `1px solid ${T.line2}`,
              fontFamily: T.font, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              {sized(Icons.up, 16, 2)}
              Withdraw
            </button>
            <button onClick={() => route(config.routes.walletActivity)} aria-label="Wallet activity" style={{
              ...btnIcon, width: 48, height: 48, borderRadius: 14, flexShrink: 0,
            }}>
              {sized(Icons.list, 18)}
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <StatTile label="Active" value={home.data?.activeorders ?? 0} dot/>
          <StatTile label="Total" value={home.data?.totalorders ?? 0}/>
        </div>

        <div style={{ ...card, padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: T.accent }}>{sized(Icons.gift, 16)}</span>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Referral Status</div>
            </div>
            <button onClick={() => route(config.routes.referrals)} style={{
              background: 'none', border: 'none', color: T.textDim, fontSize: 12,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 2,
            }}>
              View all {sized(Icons.arrR, 12, 2)}
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Mini label="Today" value={home.data?.referraloverview?.todays ?? 0} accent/>
            <Mini label="This week" value={home.data?.referraloverview?.weekly ?? 0}/>
            <Mini label="Referrals" value={home.data?.referraloverview?.referrals ?? 0} mono={false}/>
            <Mini label="Pending" value={home.data?.referraloverview?.pending ?? 0} muted/>
          </div>
        </div>

        {user && (
          <div style={{ ...card, padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12 }}>
              Account
            </div>
            <Row label="Provider" value={provider}/>
            {user.email && <Row label="Email" value={user.email}/>}
            <Row label="Referral code" value={String(user.refcode)} mono/>

            <button onClick={() => { void logout(); }} style={{
              width: '100%', marginTop: 14, padding: '13px', borderRadius: 12,
              background: 'transparent', color: T.danger,
              border: `1px solid ${T.line2}`, fontFamily: T.font, fontSize: 14, fontWeight: 600,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', gap: 8,
            }}>
              {sized(Icons.logout, 16)}
              Log out
            </button>
          </div>
        )}
      </div>

      <ReferralPrompt/>
    </div>
  );
}

function formatProvider(provider?: string | null) {
  if (!provider) return '—';
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

function StatTile({ label, value, dot }: { label: string; value: number; dot?: boolean }) {
  return (
    <div style={{ ...card, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {dot && <div style={{
          width: 6, height: 6, borderRadius: '50%', background: T.ok,
          boxShadow: `0 0 8px ${T.ok}`,
        }}/>}
        <div style={{ fontSize: 11, color: T.textMute, textTransform: 'uppercase',
          letterSpacing: 1, fontWeight: 500 }}>{label}</div>
      </div>
      <div style={{ fontFamily: T.fontMono, fontSize: 30, marginTop: 8,
        color: T.text, letterSpacing: -1, fontVariantNumeric: 'tabular-nums' }}>{fmt(value)}</div>
      <div style={{ fontSize: 11, color: T.textDim }}>{dot ? 'Orders in progress' : 'All time'}</div>
    </div>
  );
}

function Mini({ label, value, accent, muted, mono = true }: {
  label: string; value: number; accent?: boolean; muted?: boolean; mono?: boolean;
}) {
  return (
    <div>
      <div style={{ fontSize: 10, color: T.textMute, textTransform: 'uppercase',
        letterSpacing: 1, fontWeight: 500 }}>{label}</div>
      <div style={{
        fontFamily: mono ? T.fontMono : T.font, fontSize: 18, marginTop: 2,
        color: accent ? T.accent : muted ? T.textDim : T.text,
        fontVariantNumeric: 'tabular-nums', fontWeight: 500,
      }}>{fmt(value)}</div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{
      padding: '10px 0', display: 'flex', justifyContent: 'space-between', gap: 12,
      borderTop: `1px solid ${T.line}`,
    }}>
      <span style={{ fontSize: 12, color: T.textMute, textTransform: 'uppercase',
        letterSpacing: 0.8, fontWeight: 500 }}>{label}</span>
      <span style={{
        fontSize: 13, color: T.text, fontFamily: mono ? T.fontMono : T.font,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        maxWidth: '60%',
      }}>{value}</span>
    </div>
  );
}

/** True when a signed wallet link's `exp` (unix seconds) is under 2 minutes away. */
function linkExpiresSoon(url: string): boolean {
  try {
    const exp = Number(new URL(url).searchParams.get('exp'));
    return exp > 0 && exp * 1000 - Date.now() < 2 * 60_000;
  } catch {
    return false;
  }
}
